import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from './components/layouts/DashboardLayout';
import { AuthLayout } from './components/layouts/AuthLayout';

// Pages
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { HistoryPage } from './pages/history/HistoryPage';
import { ConsultationPage } from './pages/consultation/ConsultationPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { TemplateManagementPage } from './pages/settings/TemplateManagementPage';
import { ReportFormattingPage } from './pages/settings/ReportFormattingPage';
import { SubscriptionPage } from './pages/subscription/SubscriptionPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminStatsPage } from './pages/admin/AdminStatsPage';
import { AdminUserDetailPage } from './pages/admin/AdminUserDetailPage';
import { AdminSupportPage } from './pages/admin/AdminSupportPage';
import { SupportPage } from './pages/support/SupportPage';
import { LegalPage } from './pages/legal/LegalPage';

import { useAuthStore } from './stores/authStore';
import { UserRole } from './types';

export const AppRoutes = () => {
  const currentVetId = useAuthStore((state) => state.currentVetId);
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  // Require both a vet ID and a valid token — prevents the stale-currentVetId loop
  // where current_vet_id persists in localStorage after access_token is cleared by a 401.
  const isAuthenticated = !!currentVetId && !!accessToken;
  const isAdmin = user?.role === UserRole.ADMIN;

  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          isAuthenticated ? <DashboardLayout /> : <Navigate to="/login" replace />
        }
      >
        <Route path="/" element={<Navigate to="/history" replace />} />
        <Route path="/dashboard" element={<Navigate to="/history" replace />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/consultation/:consultationId" element={<ConsultationPage />} />
        <Route path="/consultation" element={<ConsultationPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/templates" element={<TemplateManagementPage />} />
        <Route path="/report-formatting" element={<ReportFormattingPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/support" element={<SupportPage />} />
      </Route>

      {/* Admin routes — require admin role */}
      <Route
        element={
          isAuthenticated && isAdmin
            ? <AdminLayout />
            : <Navigate to={isAuthenticated ? "/history" : "/login"} replace />
        }
      >
        <Route path="/admin" element={<Navigate to="/admin/stats" replace />} />
        <Route path="/admin/stats" element={<AdminStatsPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
        <Route path="/admin/support" element={<AdminSupportPage />} />
      </Route>

      {/* Public legal pages — no auth required */}
      <Route path="/cgu" element={<LegalPage file="cgu" />} />
      <Route path="/cgv" element={<LegalPage file="cgu" />} />
      <Route path="/tos" element={<LegalPage file="tos" />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to={isAuthenticated ? "/history" : "/login"} replace />} />
    </Routes>
  );
};
